/**
 * StackListView - Compact list view of all stack cards
 *
 * Shows all cards as compact rows with:
 * - Drag handle for reordering
 * - Title, word count, quality badge, source
 * - Click to navigate (switches back to card view)
 * - Delete button per row
 * - Dividers render as section headers
 *
 * @module @humanizer/studio/components/workspace/StackListView
 */

import { useCallback, useState } from 'react';
import type { StackItem } from '../../contexts/BufferSyncContext';

interface StackListViewProps {
  items: StackItem[];
  activeIndex: number;
  onNavigate: (index: number) => void;
  onRemove: (index: number) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

export function StackListView({ items, activeIndex, onNavigate, onRemove, onReorder }: StackListViewProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== toIndex) {
      onReorder(dragIndex, toIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  return (
    <div className="stack-list">
      {items.map((item, i) => {
        if (item.isDivider) {
          return (
            <div
              key={item.bufferId}
              className={`stack-list__divider${dragOverIndex === i ? ' stack-list__row--drag-over' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={(e) => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
            >
              <span className="stack-list__drag-handle" title="Drag to reorder">&#x2630;</span>
              <span className="stack-list__divider-title">{item.dividerTitle ?? 'Section'}</span>
              <button
                className="stack-list__remove"
                onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                title="Remove"
                aria-label="Remove section"
              >
                &times;
              </button>
            </div>
          );
        }

        const tierBadge = item.qualityScore != null
          ? item.qualityScore >= 0.7 ? '\u2605'
          : item.qualityScore >= 0.4 ? '\u25C6'
          : '\u25C7'
          : null;

        return (
          <div
            key={item.bufferId}
            className={`stack-list__row${i === activeIndex ? ' stack-list__row--active' : ''}${dragOverIndex === i ? ' stack-list__row--drag-over' : ''}${dragIndex === i ? ' stack-list__row--dragging' : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDrop={(e) => handleDrop(e, i)}
            onDragEnd={handleDragEnd}
            onClick={() => onNavigate(i)}
          >
            <span className="stack-list__drag-handle" title="Drag to reorder">&#x2630;</span>
            <span className="stack-list__index">{i + 1}</span>
            <span className="stack-list__title" title={item.summary}>{item.summary}</span>
            {tierBadge && (
              <span className="stack-list__badge" title={`Quality: ${Math.round((item.qualityScore ?? 0) * 100)}%`}>
                {tierBadge}
              </span>
            )}
            <span className="stack-list__words">{item.wordCount.toLocaleString()}</span>
            {item.source && (
              <span className="stack-list__source">{item.source}</span>
            )}
            <button
              className="stack-list__remove"
              onClick={(e) => { e.stopPropagation(); onRemove(i); }}
              title="Remove"
              aria-label="Remove card"
            >
              &times;
            </button>
          </div>
        );
      })}
    </div>
  );
}
